import { useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import {
  HOME_TILE_GROUPS,
  HOME_TILE_INFO,
  type HomeTileId,
} from "@/pages/home/homeLayout";

export function HomeTileCatalogDialog({
  open,
  onClose,
  permittedIds,
  visibleIds,
  onToggleTile,
}: {
  open: boolean;
  onClose: () => void;
  permittedIds: HomeTileId[];
  visibleIds: ReadonlySet<HomeTileId>;
  onToggleTile: (id: HomeTileId) => void;
}) {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();

  const groups = useMemo(() => {
    return HOME_TILE_GROUPS.map((group) => ({
      group,
      tiles: permittedIds.filter((id) => {
        const info = HOME_TILE_INFO[id];
        if (info.group !== group) return false;
        if (!needle) return true;
        return (
          info.label.toLowerCase().includes(needle) ||
          info.description.toLowerCase().includes(needle)
        );
      }),
    })).filter((entry) => entry.tiles.length > 0);
  }, [needle, permittedIds]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Kacheln"
      description="Nur Bereiche, die du sehen darfst. Die Auswahl wird nur für dich gespeichert."
      bodyClassName="max-h-[min(70vh,40rem)] overflow-y-auto !p-0"
    >
      <div className="eh-home-catalog">
        <div className="eh-home-catalog__search">
          <input
            className="eh-input w-full"
            type="search"
            value={query}
            placeholder="Kachel suchen"
            aria-label="Kachel suchen"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        {groups.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-label-secondary">
            Keine passende Kachel.
          </p>
        ) : (
          groups.map((entry) => (
            <section key={entry.group} className="eh-home-catalog__group" aria-label={entry.group}>
              <h3 className="eh-home-catalog__heading">{entry.group}</h3>
              <ul className="eh-home-catalog__list">
                {entry.tiles.map((id) => {
                  const info = HOME_TILE_INFO[id];
                  const active = visibleIds.has(id);
                  return (
                    <li key={id} className={clsx("eh-home-catalog__item", active && "is-on")}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-label">{info.label}</p>
                        <p className="mt-0.5 text-sm text-label-secondary">{info.description}</p>
                      </div>
                      <button
                        type="button"
                        className={clsx("eh-home-catalog__toggle", active && "is-on")}
                        aria-pressed={active}
                        onClick={() => onToggleTile(id)}
                      >
                        {active ? "Entfernen" : "Hinzufügen"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>
    </Dialog>
  );
}
